import React from 'react';
import { usePayments } from '../../../services/billing';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

/** Format a minor-unit (cents) amount with its currency, e.g. €149.00. */
const formatAmount = (amountMinor: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountMinor / 100);
  } catch {
    // Unknown currency code — fall back to a plain formatted number.
    return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** Revolut payment states map onto the shared success/warning/error palette. */
const getStateBadgeColor = (state: string): string => {
  switch (state.toLowerCase()) {
    case 'completed':
    case 'paid':
    case 'succeeded':
      return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400';
    case 'pending':
    case 'processing':
    case 'authorised':
      return 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-400';
    case 'failed':
    case 'declined':
    case 'cancelled':
      return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-400';
    default:
      return 'bg-grey-100 text-grey-800 dark:bg-grey-950/20 dark:text-grey-400';
  }
};

const titleCase = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/**
 * Payment history for the active tenant (Revolut). Revolut has no invoice PDFs,
 * so this lists date / description / amount / state only.
 */
export default function PaymentsHistory() {
  const { theme } = useTheme();
  const { data: payments = [], isLoading, isError } = usePayments();

  return (
    <phantom-ui {...phantomProps(theme === 'dark', isLoading && payments.length === 0)}>
      <div className="bg-surface-primary rounded-lg p-6 border border-border-primary">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Payment History</h3>

        {isLoading && payments.length === 0 && (
          <div className="space-y-4">
            <div className="h-10 rounded w-full" />
            <div className="h-10 rounded w-full" />
            <div className="h-10 rounded w-full" />
          </div>
        )}

        {isError && (
          <div className="mb-6 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
            <p className="text-sm text-error-800 dark:text-error-200">
              Failed to load payment history. Please try again.
            </p>
          </div>
        )}

        {!isError && payments.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-text-secondary">No payments yet.</p>
            <p className="text-sm text-text-tertiary mt-1">
              Payments will appear here once you subscribe to a paid plan.
            </p>
          </div>
        )}

        {!isError && payments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-grey-50 dark:bg-grey-950/50 border-b border-border-primary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    State
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {payments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="hover:bg-grey-50 dark:hover:bg-grey-950/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-text-primary">
                        {formatDate(payment.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-secondary">
                        {payment.description || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-semibold text-text-primary">
                        {formatAmount(payment.amount, payment.currency)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStateBadgeColor(
                          payment.state
                        )}`}
                      >
                        {titleCase(payment.state)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </phantom-ui>
  );
}
